import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import React, { useState } from "react"

import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import { cn } from "@/lib/utils"
import { convertIpfsUrl } from "@/lib/utils"
import multiavatar from "@multiavatar/multiavatar"

interface NFTAvatar {
  avatar_url?: string
  avatar_crop_url?: string
  animation_url?: string
}

interface UserAvatarProps extends React.ComponentProps<typeof Avatar> {
  isLoading?: boolean
  avatarUrl?: string
  nftAvatar?: NFTAvatar
  address: string
  size?: number
  wrapperClassName?: string
  avatarImageProps?: Omit<React.ComponentProps<typeof AvatarImage>, "src">
  avatarFallbackProps?: Omit<
    React.ComponentProps<typeof AvatarFallback>,
    "children"
  >
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  address,
  avatarUrl,
  nftAvatar,
  size: sizeProp,
  avatarFallbackProps,
  avatarImageProps,
  wrapperClassName,
  ...props
}) => {
  const svgMultiavatar = multiavatar(address.toLowerCase())
  const [imageLoaded, setImageLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  // Avatar priority: NFT avatar > regular avatar > multiavatar
  const hasNftAvatar = Boolean(
    nftAvatar?.animation_url ||
    nftAvatar?.avatar_crop_url ||
    nftAvatar?.avatar_url
  )
  const hasRegularAvatar = Boolean(avatarUrl)

  // Get NFT avatar URL
  const nftAvatarUrl = hasNftAvatar
    ? convertIpfsUrl(
        (
          nftAvatar?.animation_url ??
          nftAvatar?.avatar_crop_url ??
          nftAvatar?.avatar_url ??
          ""
        ).replace("?w=500&auto=format", "")
      )
    : ""

  // Use size prop if provided, otherwise parse from className, default to 24
  const sizeMatch = props.className?.match(/(?:w|size)-\[?(\d+)/)
  const size = sizeProp ?? (sizeMatch ? parseInt(sizeMatch[1]) : 24)

  // If NFT avatar, render hexagon avatar (simplified version)
  if (hasNftAvatar && nftAvatarUrl) {
    return (
      <div
        className={cn(
          "relative flex items-center justify-center",
          wrapperClassName
        )}
        style={{
          width: `${size}px`,
          height: `${size}px`,
        }}
      >
        <div
          className="overflow-hidden rounded-full"
          style={{
            width: `${size}px`,
            height: `${size}px`,
          }}
        >
          <ImageWithFallback
            src={nftAvatarUrl}
            alt="NFT Avatar"
            width={size}
            height={size}
            className="h-full w-full object-cover"
            fill
            baseClassName="w-full h-full"
          />
        </div>
      </div>
    )
  }

  // Regular avatar or multiavatar fallback
  return (
    <div className={cn(wrapperClassName)}>
      <Avatar
        {...props}
        className={cn(
          "bg-background border-background relative overflow-hidden",
          props.className
        )}
      >
        {hasRegularAvatar && (
          <AvatarImage
            {...avatarImageProps}
            src={avatarUrl}
            onError={() => {
              setHasError(true)
              setImageLoaded(false)
            }}
            onLoad={() => {
              setImageLoaded(true)
              setHasError(false)
            }}
          />
        )}
        {(!hasRegularAvatar || hasError || !imageLoaded) && (
          <AvatarFallback
            {...avatarFallbackProps}
            className={cn(
              "border-border absolute inset-0 rounded-full border bg-transparent",
              avatarFallbackProps?.className
            )}
          >
            <div
              className="size-full rounded-full"
              dangerouslySetInnerHTML={{ __html: svgMultiavatar }}
            />
          </AvatarFallback>
        )}
      </Avatar>
    </div>
  )
}
